import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { MaybeUndefined } from '@lmt/unicorn-shared/app/helpers/typeUtils';
import clsx from 'clsx';
import { addMilliseconds, isValid, parse, startOfToday } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComboBoxInput } from '../dropdown/ComboBoxInput2';

import styles from './TimePicker.module.scss';

interface TimePickerProps {
  minTimeInMills?: number;
  timeInMills: number | null | undefined;
  onTimeChanged?: (newTime: number | null, isInvalid: boolean) => void;
  error?: boolean;
  errorText?: string;
  timeEntriesIntervalInMinutes?: number;
  className?: string;
  variant?: 'normal' | 'formInput';
  disableInternalErrors?: boolean;
  ignoreMinTimeError?: boolean;
  /*
  Called when user typed something wrong (e.g. 'asd') and leaves the field (onBlur).
  Form that owns this control shall account the error state for validation purposes.
  When user corrects his input, `onTimeChanged` will be called.
   */
  onInvalidTime?: () => void;
  testId?: string;
  disabled?: boolean;
  allowMidnightEndTime?: boolean;
}

interface TimeEntry {
  timeInMills: number;
  label: string;
}

const timeFormat = () =>
  localFormat(new Date(2222, 11, 23, 22, 15, 16), 'p')
    .replace('22', 'HH')
    .replace('10', 'hh')
    .replace('15', 'mm')
    .replace('16', 'ss')
    .replace(/pm/i, 'a');

function parseTime(timeString: string): number | null {
  if (timeString == '') return null;

  const referenceDate = new Date();
  const parsedTime = parse(timeString, timeFormat(), referenceDate);
  if (!isValid(parsedTime)) {
    return null;
  }

  const hours = parsedTime.getHours();
  const minutes = parsedTime.getMinutes();
  //convert to milliseconds
  return (hours * 60 + minutes) * 60000;
}

function millsToTimeString<T extends number | undefined | null>(
  timeInMills: T,
): string | MaybeUndefined<T> {
  if (timeInMills === null) return null as MaybeUndefined<T>;
  if (timeInMills === undefined) return undefined as MaybeUndefined<T>;

  return localFormat(addMilliseconds(startOfToday(), timeInMills), 'p');
}

export const TimePicker: FC<TimePickerProps> = (props) => {
  const {
    minTimeInMills,
    onTimeChanged,
    timeInMills,
    errorText,
    timeEntriesIntervalInMinutes = 30,
    disableInternalErrors = false,
    ignoreMinTimeError = false,
    className,
    variant,
    allowMidnightEndTime,
  } = props;
  const i18n = useTranslation();
  const [isInvalidTimeError, setIsInvalidTimeError] = useState(false);
  // If set, this error is displayed instead of props.error.
  // Is used for Invalid Time error
  const [overriddenError, setOverriddenError] = useState<string | null>(null);
  const [previousValue, setPreviousValue] = useState<string>();

  useEffect(
    function resetOverriddenErrorWhenValidationSucceeds() {
      if (!isInvalidTimeError) {
        setOverriddenError(null);
      }
    },
    [isInvalidTimeError],
  );

  useEffect(
    function resetErrorsWhenTimeInMillsChanges() {
      if (timeInMills) {
        setIsInvalidTimeError(false);
      }
    },
    [timeInMills],
  );

  const timeEntries: TimeEntry[] = useMemo(() => {
    const key =
      timeEntriesIntervalInMinutes +
      (allowMidnightEndTime ?? false).toString() +
      i18n.i18n.language;
    if (!timeIntervalsCached[key]) {
      timeIntervalsCached[key] = generateTimeEntriesForWholeDay(
        timeEntriesIntervalInMinutes,
        allowMidnightEndTime,
      );
    }
    return timeIntervalsCached[key].filter(
      (entry) => entry.timeInMills >= (minTimeInMills || 0),
    );
  }, [minTimeInMills, i18n.i18n.language, allowMidnightEndTime]);

  const onChange = useCallback(
    (value: string | TimeEntry | null) => {
      setOverriddenError(null);
      setIsInvalidTimeError(false);
      if (value === null) {
        onTimeChanged?.(null, false);
      } else if (typeof value === 'string') {
        let newTime = parseTime(value);
        if (
          !ignoreMinTimeError &&
          minTimeInMills !== undefined &&
          newTime !== null &&
          newTime < minTimeInMills
        ) {
          newTime = null;
        }
        if (newTime === null) {
          // time is invalid
          setIsInvalidTimeError(true);
          setOverriddenError(i18n.t('errors.invalid_time'));
          onTimeChanged?.(null, true);
          setPreviousValue(value);
          props.onInvalidTime?.();
        } else {
          onTimeChanged?.(newTime, false);
        }
      } else {
        onTimeChanged?.(value.timeInMills, false);
      }
    },
    [onTimeChanged, minTimeInMills],
  );

  // const defaultSelectedTimeEntry = useMemo(() => {
  //   return timeEntries.find((x) => x.timeInMills === timeInMills);
  // }, [timeEntries, timeInMills]);
  return (
    <div className={clsx(styles.container, className)}>
      <ComboBoxInput
        options={timeEntries}
        getOptionLabel={'label'}
        value={
          timeEntries.find((x) => x.timeInMills === timeInMills) ??
          (timeInMills !== null && timeInMills !== undefined
            ? isNaN(timeInMills)
              ? previousValue
              : millsToTimeString(timeInMills)
            : '')
        }
        isOptionEqualToValue={(item1: TimeEntry, item2: TimeEntry) =>
          item1?.timeInMills === item2?.timeInMills
        }
        variant={variant ?? 'formInput'}
        errorText={
          props.disabled
            ? undefined
            : disableInternalErrors
              ? errorText
              : (overriddenError ?? errorText)
        }
        onValueChanged={onChange}
        testId={props.testId}
        disabled={props.disabled}
      />
    </div>
  );
};

const timeIntervalsCached: Record<string, TimeEntry[]> = {};

function generateTimeEntriesForWholeDay(
  timeIntervalInMinutes: number,
  allowMidnightEndTime: boolean = false,
): TimeEntry[] {
  const totalMillsInADay = 24 * 60 * 60000;
  const intervalInMills = timeIntervalInMinutes * 60000;
  const entries: TimeEntry[] = [];
  for (
    let timeInMills = 0;
    allowMidnightEndTime
      ? timeInMills <= totalMillsInADay
      : timeInMills < totalMillsInADay;
    timeInMills += intervalInMills
  ) {
    const label = millsToTimeString(timeInMills);
    entries.push({
      timeInMills,
      label: label,
    });
  }
  return entries;
}
